import React from 'react';

import PrivateHireQuickEstimatesForm from '@zego/flows/quickEstimates/privateHire/forms/PrivateHireQuickEstimatesForm';
import BaseLayout from '@zego/layouts/BaseLayout';
import LogoSection from '@zego/pageContainers/gb/PrivateHirePartnersGb/components/LogoSection.js';
import { unit, unitTRBL } from '@zego/theme';
import { TrackingScope } from '@zego/tracking';

import Spacer from '@zego/components/atoms/Spacer';
import {
  CLAIMS,
  DOCUMENTS,
  PAYMENTS,
  SUPPORT,
  TAXI,
} from '@zego/components/Faqs/constants';
import Faqs from '@zego/components/Faqs/Faqs';
import Head from '@zego/components/Head';
import PrivateHireComparisonTable from '@zego/components/privateHire/PrivateHireComparisonTable';
import PrivateHirePolicyBenefits from '@zego/components/privateHire/PrivateHirePolicyBenefits';
import PrivateHirePricingBreakdown from '@zego/components/privateHire/PrivateHirePricingBreakdown';
import PrivateHireSenseEducationalSection from '@zego/components/privateHire/PrivateHireSenseEducationalSection';
import PrivateHireWidget from '@zego/components/privateHire/PrivateHireWidget';
import PrivateHireWorkProviders from '@zego/components/privateHire/PrivateHireWorkProviders';
import ProductHeroSection from '@zego/components/ProductHeroSection';

import socialPrivateHireVer2 from '@zego/static/images/social/social-private-hire-ver2.jpg';
import heroImage from '@zego/static/private-hire/hero-private-hire-v3.jpg';

const FAQS_CONTENT = {
  title: 'Find answers quickly',
  rowsData: [TAXI, CLAIMS, PAYMENTS, DOCUMENTS, SUPPORT],
};

/**
 * @param {Object} props
 * @param {unknown} props.viewer
 * @param {string} props.title
 * @param {string} props.description
 * @param {React.Node[]} props.benefits
 * @param {string} props.logo
 * @param {string} props.logoAlt
 * @param {import("@emotion/styled").CSSObject['top']} props.logoTop
 * @param {import("@emotion/styled").CSSObject['height']} props.logoHeight
 * @param {string} props.locationName
 * @param {string} [props.source]
 * @param {string} [props.productHeroTitle]
 * @param {string[]} [props.footnotes]
 * @param {import("@emotion/styled").CSSObject['marginTop']} [props.titleMarginTop]
 */
const PrivateHirePartnersBase = ({
  viewer,
  title,
  description,
  benefits,
  footnotes,
  logo,
  logoAlt,
  logoTop,
  logoHeight,
  locationName,
  source,
  titleMarginTop,
  productHeroTitle = 'Fairer Private Hire insurance, driven by you',
}) => {
  return (
    <BaseLayout
      viewer={viewer}
      title={title}
      description={description}
      hideHeader={true}
    >
      <Head>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Zego private hire insurance" />
        <meta
          name="twitter:image"
          content={`https://www.zego.com${socialPrivateHireVer2}`}
        />
        <meta property="og:image" content={socialPrivateHireVer2} />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="536" />
      </Head>
      <LogoSection
        logo={logo}
        logoTop={logoTop}
        logoHeight={logoHeight}
        logoAlt={logoAlt}
      />
      <ProductHeroSection
        title={productHeroTitle}
        heroImage={heroImage}
        benefits={benefits}
        footnotes={footnotes}
        margin={unitTRBL(-5, 0, 0, 0)}
        titleMarginTop={titleMarginTop}
      >
        <TrackingScope locationName={locationName}>
          <PrivateHireQuickEstimatesForm viewer={viewer} source={source} />
        </TrackingScope>
      </ProductHeroSection>
      <PrivateHireSenseEducationalSection />
      <PrivateHirePolicyBenefits />
      <PrivateHireComparisonTable />
      <PrivateHireWorkProviders />
      <PrivateHirePricingBreakdown />
      <PrivateHireWidget
        viewer={viewer}
        locationName={locationName}
        source={source}
      />
      <Spacer height={unit(11)} />
      <Faqs {...FAQS_CONTENT} />
      <Spacer height={unit(10)} />
    </BaseLayout>
  );
};

export default PrivateHirePartnersBase;
